/**
 * QuestionImage.tsx
 * Enhanced question image component with modal and responsive handling
 */

import { useState } from 'react';
import { ZoomIn, Image as ImageIcon } from 'lucide-react';
import { cn } from '@vsat/shared/lib/utils';
import ImageModal from './ImageModal';

interface Props {
  src: string;
  alt?: string;
  className?: string;
  showZoomHint?: boolean;
}

export default function QuestionImage({
  src,
  alt = 'Hình ảnh câu hỏi',
  className,
  showZoomHint = true,
}: Props) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageError, setImageError] = useState(false);

  if (imageError) {
    return (
      <div
        className={cn(
          'flex items-center justify-center p-8 border-2 border-dashed border-border rounded-lg bg-gray-50 dark:bg-gray-800',
          className
        )}
      >
        <div className='text-center text-muted-foreground'>
          <ImageIcon className='w-12 h-12 mx-auto mb-2 opacity-50' />
          <p className='text-sm'>Không thể tải hình ảnh</p>
          <p className='text-xs text-muted-foreground dark:text-gray-500 mt-1'>{src}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={cn('relative group', className)}>
        <img
          src={src}
          alt={alt}
          className={cn(
            'max-w-full h-auto rounded-lg border border-border shadow-sm',
            'cursor-pointer hover:border-primary/30 hover:shadow-lg transition-all duration-300',
            'group-hover:scale-[1.02] transform group-hover:ring-2 group-hover:ring-primary/200 dark:group-hover:ring-primary/800'
          )}
          onClick={() => setIsModalOpen(true)}
          onError={() => setImageError(true)}
          loading='lazy'
        />

        {/* Zoom hint overlay */}
        {showZoomHint && (
          <div className='absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/20 rounded-lg'>
            <div className='bg-white/90 dark:bg-gray-800/90 rounded-full p-2 shadow-lg border border-primary/40'>
              <ZoomIn className='w-5 h-5 text-primary' />
            </div>
          </div>
        )}

        {/* Image caption if alt text is descriptive */}
        {alt && alt !== 'Hình ảnh câu hỏi' && (
          <div className='mt-2 text-sm text-muted-foreground text-center italic'>{alt}</div>
        )}
      </div>

      <ImageModal src={src} alt={alt} isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}
